/**
 * State Machine Demo Page
 * 
 * Demonstrates the complete state machine system with quote lifecycle management.
 * Shows integration between React components and backend state machine services.
 */

import React, { useState } from 'react';
import { Card, Button, TextInput } from '@etherisc/ui-kit';
import { useStateMachine } from '../../hooks/state-machine/useStateMachine';
import { StateActionButtons } from '../../components/state-machine/StateActionButtons';
import { StateVisualization } from '../../components/state-machine/StateVisualization';

export const StateMachineDemo: React.FC = () => {
  const [selectedQuoteId, setSelectedQuoteId] = useState<string>('');
  const [demoQuoteId, setDemoQuoteId] = useState<string>('');

  // State machine hook (always called, but only active when quote is selected)
  const stateMachine = useStateMachine('quote', selectedQuoteId || 'placeholder');

  const handleCreateDemoQuote = async () => {
    try {
      // For demo purposes, we'll use a simulated quote ID that won't make API calls
      // In a real implementation, this would create an actual quote
      const mockQuoteId = `demo-quote-${Date.now()}`;
      setDemoQuoteId(mockQuoteId);
      setSelectedQuoteId(mockQuoteId);
      
      // Show a helpful message about the demo mode
      console.log('📝 Demo quote created:', mockQuoteId);
      console.log('💡 This is a demo mode - no real API calls will be made');
    } catch (error) {
      console.error('Failed to create demo quote:', error);
    }
  };

  const handleTransition = async (event: string, payload?: unknown) => {
    if (!stateMachine) return;

    try {
      await stateMachine.transition(event, { payload });
      console.log(`✅ Transition ${event} executed successfully`);
    } catch (error) {
      console.error(`❌ Transition ${event} failed:`, error);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-4">State Machine System Demo</h1>
        <p className="text-gray-600 mb-6">
          This demo showcases the vendor-neutral, database-authoritative state machine system.
          The system enforces valid transitions at the database level while providing
          a React-friendly interface for UI development.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Control Panel */}
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Control Panel</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Quote ID
                </label>
                <TextInput
                  value={selectedQuoteId}
                  onChange={(e) => setSelectedQuoteId(e.target.value)}
                  placeholder="Enter quote ID or create demo quote"
                />
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleCreateDemoQuote}
                  variant="secondary"
                >
                  Create Demo Quote
                </Button>
                
                {selectedQuoteId && selectedQuoteId !== 'placeholder' && (
                  <Button 
                    onClick={stateMachine.refresh}
                    variant="secondary"
                    disabled={stateMachine.state.loading}
                  >
                    Refresh State
                  </Button>
                )}
              </div>

              {demoQuoteId && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                  <div className="text-green-600 font-medium mb-1">
                    ✅ Demo quote created: {demoQuoteId}
                  </div>
                  <div className="text-blue-700">
                    💡 This is a demo mode with simulated state transitions. 
                    Try the action buttons below to see the state machine in action!
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* State Information */}
        {selectedQuoteId && selectedQuoteId !== 'placeholder' && (
          <StateVisualization
            processKey={stateMachine.state.processKey}
            currentState={stateMachine.state.state}
            nextEvents={stateMachine.state.nextEvents}
            businessKey={stateMachine.state.businessKey}
            version={stateMachine.state.version}
            history={stateMachine.history}
          />
        )}
      </div>

      {/* Action Buttons */}
      {selectedQuoteId && selectedQuoteId !== 'placeholder' && (
        <Card className="mt-6">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Available Actions</h3>
            
            {stateMachine.state.error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                Error: {stateMachine.state.error}
              </div>
            )}

            <StateActionButtons
              nextEvents={stateMachine.state.nextEvents}
              onTransition={handleTransition}
              loading={stateMachine.state.loading}
            />

            {stateMachine.state.nextEvents.length === 0 && (
              <div className="text-gray-500 text-sm">
                This quote has reached a final state. No further actions are available.
              </div>
            )}
          </div>
        </Card>
      )}

      {/* System Information */}
      <Card className="mt-6">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">System Architecture</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-green-600 mb-2">✅ Database-Authoritative</h4>
              <p className="text-gray-600">
                PostgreSQL enforces all state transitions via triggers. 
                Invalid transitions are rejected at the database level.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-blue-600 mb-2">🔄 Vendor-Neutral</h4>
              <p className="text-gray-600">
                State machine definitions are stored as JSON. 
                XState is just an adapter and can be replaced.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-purple-600 mb-2">📋 BPMN-Ready</h4>
              <p className="text-gray-600">
                Stable element IDs, lanes, and business keys enable 
                future migration to BPMN/Camunda.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Technical Details */}
      <Card className="mt-6">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Technical Implementation</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            <div>
              <h4 className="font-medium mb-2">Features Implemented:</h4>
              <ul className="space-y-1 text-gray-600">
                <li>• MachineSpec v2 JSON Schema validation</li>
                <li>• XState v5 adapter with StateEngine interface</li>
                <li>• Database enforcement triggers</li>
                <li>• Optimistic locking and idempotency</li>
                <li>• Outbox pattern for events</li>
                <li>• Timer scheduling (DURATION/DATE)</li>
                <li>• Immutable audit history</li>
                <li>• React integration hooks</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">Architecture Principles:</h4>
              <ul className="space-y-1 text-gray-600">
                <li>• PostgreSQL is the single source of truth</li>
                <li>• React state is strictly advisory</li>
                <li>• All business logic in database/JSON</li>
                <li>• Session variables for trigger context</li>
                <li>• Exactly-once event processing</li>
                <li>• RBAC integration ready</li>
                <li>• Structured logging and metrics ready</li>
                <li>• Future BPMN migration support</li>
              </ul>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};
